import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { NavLink } from 'react-router';

import { cn } from '@/shared/lib/cn';
import { Icon, type IconName } from '@/shared/ui/shell';

interface HeaderMenuProps {
  label: string;
  /** The header the menu hangs from: a press on it is the header's business, not a reason to close. */
  header: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
}

/** A panel under the header, opened by one of its buttons. Escape and a press elsewhere close it. */
export function HeaderMenu({ label, header, onClose, children }: HeaderMenuProps) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = (event: PointerEvent) => {
      // The header's own buttons open, switch and close the menus themselves.
      if (!header.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    panel.current?.querySelector<HTMLElement>('a, button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [header, onClose]);

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={label}
      className="absolute top-full right-3 z-30 mt-2 flex w-72 max-w-[calc(100vw-1.5rem)] flex-col gap-1 rounded-3xl border border-line bg-surface p-3 shadow-xl"
    >
      {children}
    </div>
  );
}

const row = 'flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-left text-sm font-semibold';

/** A page to go to from a menu. */
export function MenuLink({ to, icon, end, onClick, children }: { to: string; icon: IconName; end?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => cn(row, isActive ? 'bg-brand-soft text-brand-strong' : 'text-muted hover:bg-sunken hover:text-ink')}
    >
      <Icon name={icon} size={18} />
      {children}
    </NavLink>
  );
}

/** Something to do from a menu. */
export function MenuAction({ icon, onClick, children }: { icon: IconName; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={cn(row, 'text-muted hover:bg-sunken hover:text-ink')}>
      <Icon name={icon} size={18} />
      {children}
    </button>
  );
}
